import { type ClassValue, clsx } from "clsx"
import { extendTailwindMerge } from "tailwind-merge"

/**
 * `tailwind-merge` no conocia la escala `ds-` del design system, y ante una clase
 * que no reconoce adivina el grupo al que pertenece. Con `text-ds-500` adivinaba
 * mal: la metia en el grupo del color de texto, decidia que competia con
 * `text-canvas` y se quedaba con la ultima. Resultado: TODAS las variantes del
 * primitivo de boton perdian su clase de color al combinarse con un tamano.
 *
 * El fallo era invisible en cuatro de las cinco variantes, porque el color que
 * heredaban del contenedor era justo el que querian. La quinta, `inverse`, pinta
 * tinta sobre tinta: el CTA del plan destacado de `/precios` medi­a 1:1 —texto
 * invisible— en los DOS temas, no solo en oscuro.
 *
 * Con las escalas declaradas el mecanismo funciona en ambos sentidos: el color
 * deja de perderse, y un llamante que pase `px-ds-8` por `className` gana de
 * verdad sobre el `px-ds-6` del primitivo en vez de dejar las dos clases puestas
 * y que decida el orden del stylesheet.
 *
 * Las listas replican las de `tailwind.config.ts`. Añadir un peldaño alli y no
 * aqui reabre el bug, y por eso `src/__tests__/design-system/
 * clases-sobreviven-al-merge.test.ts` compara las dos fuentes.
 */
const ESCALA_TEXTO = ['ds-100', 'ds-200', 'ds-300', 'ds-400', 'ds-500', 'ds-600', 'ds-700']
const ESCALA_ESPACIO = ['ds-1', 'ds-2', 'ds-3', 'ds-4', 'ds-6', 'ds-8', 'ds-12', 'ds-16']
// Fase 17: las sombras del lienzo 2.0 (chips, acentos azules y halos).
const ESCALA_SOMBRA = [
    'ds-1', 'ds-2', 'ds-3', 'ds-4',
    'ds-chip', 'ds-chip-atenuada', 'ds-acento-2', 'ds-acento-3',
    'ds-halo-activo', 'ds-halo-activo-movil', 'ds-halo-paciente', 'ds-halo-paciente-movil',
]
const ESCALA_RADIO = ['ds-sm', 'ds-md', 'ds-lg', 'ds-pill']
const ESCALA_DURACION = ['ds-fast', 'ds-base', 'ds-slow']

const twMerge = extendTailwindMerge({
    extend: {
        // `spacing` alimenta de una vez todos los grupos que lo usan (p, m, gap,
        // w, h, min-h…), en vez de enumerarlos uno por uno y olvidar el siguiente.
        theme: { spacing: ESCALA_ESPACIO },
        classGroups: {
            'font-size': [{ text: ESCALA_TEXTO }],
            shadow: [{ shadow: ESCALA_SOMBRA }],
            rounded: [{ rounded: ESCALA_RADIO }],
            duration: [{ duration: ESCALA_DURACION }],
        },
    },
})

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs))
}

/**
 * Se exportan solo para que el test guardian pueda comparar estas listas con las
 * de `tailwind.config.ts`. No las use codigo de producto: la escala se consume
 * escribiendo las clases, no leyendo el array.
 */
export const ESCALAS_DS = {
    fontSize: ESCALA_TEXTO,
    spacing: ESCALA_ESPACIO,
    boxShadow: ESCALA_SOMBRA,
    borderRadius: ESCALA_RADIO,
    transitionDuration: ESCALA_DURACION,
} as const
